import * as THREE from 'three';
import type { MatchConfig, Vec3 } from '../../core/contracts';
import { PITCH } from '../../core/math';
import { StaticBuilder, TriSoup, prng } from './util';
import { BOARDS, DUGOUT, GROUND } from './pitch';
import { LED_PANEL_METRES } from './textures';

/** Rounded-rectangle bowl path the stands are swept along. */
export const BOWL = { A: GROUND.halfX, B: GROUND.halfZ, R: 18 } as const;

interface PathSample { x: number; z: number; nx: number; nz: number }

function bowlPath(): PathSample[] {
  const { A, B, R } = BOWL;
  const cx = A - R, cz = B - R;
  const out: PathSample[] = [];
  const corners: [number, number, number][] = [[cx, cz, 0], [-cx, cz, 90], [-cx, -cz, 180], [cx, -cz, 270]];
  const ARC = 14, STRAIGHT = 8;
  for (let k = 0; k < 4; k++) {
    const [ox, oz, a0] = corners[k]!;
    for (let i = 0; i <= ARC; i++) {
      const a = ((a0 + (90 * i) / ARC) * Math.PI) / 180;
      out.push({ x: ox + Math.cos(a) * R, z: oz + Math.sin(a) * R, nx: Math.cos(a), nz: Math.sin(a) });
    }
    const last = out[out.length - 1]!;
    const [nx2, nz2] = corners[(k + 1) % 4]!;
    const a1 = ((a0 + 90) * Math.PI) / 180;
    const ex = nx2 + Math.cos(a1) * R, ez = nz2 + Math.sin(a1) * R;
    for (let i = 1; i < STRAIGHT; i++) {
      const t = i / STRAIGHT;
      out.push({ x: last.x + (ex - last.x) * t, z: last.z + (ez - last.z) * t, nx: last.nx, nz: last.nz });
    }
  }
  return out;
}

interface ProfileEdge { a: [number, number]; b: [number, number]; color: THREE.Color; facing: [number, number] }

export const LOWER = { rows: 20, d0: 0.6, h0: 1.1, depth: 0.8, rise: 0.4 } as const;
export const UPPER = { rows: 24, d0: 15.2, h0: 12.6, depth: 0.85, rise: 0.5 } as const;
export const FASCIA = { d: 14.5, h0: 10.3, h1: 12.6 } as const;
export const ROOF = { frontD: 6, frontH: 26.4 } as const;

export interface StadiumBuild {
  /** Packed seats: x, y, z, facingAngle, sectionCode (0 home end, 1 away end, 2 near side, 3 far side). */
  seats: Float32Array;
  seatCount: number;
  /** Floodlight lamp glow positions (xyz) + size. */
  lamps: number[];
  ledGeometry: THREE.BufferGeometry;
  bowlGeometry: THREE.BufferGeometry;
  anchors: Record<string, Vec3>;
}

export function buildStadium(sb: StaticBuilder, config: MatchConfig): StadiumBuild {
  const path = bowlPath();
  const n = path.length;
  const C = (h: string) => new THREE.Color(h);
  const seatCol = C(config.home.primaryColor).lerp(C('#2b2e35'), 0.45);
  const seatCol2 = seatCol.clone().multiplyScalar(0.8);
  const riser = C('#3a3e46'), concrete = C('#80848b'), dark = C('#1f2328');
  const edges: ProfileEdge[] = [];
  const E = (a: [number, number], b: [number, number], color: THREE.Color, facing: [number, number]) => edges.push({ a, b, color, facing });

  E([0, 0], [0, LOWER.h0], C('#15191e'), [-1, 0]);
  E([0, LOWER.h0], [LOWER.d0, LOWER.h0], concrete, [0, 1]);
  for (let i = 0; i < LOWER.rows; i++) {
    const d = LOWER.d0 + LOWER.depth * i, h = LOWER.h0 + LOWER.rise * i;
    E([d, h], [d, h + LOWER.rise], riser, [-1, 0]);
    E([d, h + LOWER.rise], [d + LOWER.depth, h + LOWER.rise], i % 2 ? seatCol : seatCol2, [0, 1]);
  }
  const lowEndD = LOWER.d0 + LOWER.depth * LOWER.rows, lowEndH = LOWER.h0 + LOWER.rise * LOWER.rows;
  E([lowEndD, lowEndH], [lowEndD, FASCIA.h0], C('#2a2e35'), [-1, 0]);
  E([lowEndD, FASCIA.h0], [FASCIA.d, FASCIA.h0], C('#30343b'), [0, -1]);
  E([FASCIA.d, FASCIA.h0], [FASCIA.d, FASCIA.h1], dark, [-1, 0]);
  E([FASCIA.d, FASCIA.h1], [UPPER.d0, UPPER.h0], concrete, [0, 1]);
  for (let j = 0; j < UPPER.rows; j++) {
    const d = UPPER.d0 + UPPER.depth * j, h = UPPER.h0 + UPPER.rise * j;
    E([d, h], [d, h + UPPER.rise], riser, [-1, 0]);
    E([d, h + UPPER.rise], [d + UPPER.depth, h + UPPER.rise], j % 2 ? seatCol : seatCol2, [0, 1]);
  }
  const upEndD = UPPER.d0 + UPPER.depth * UPPER.rows, upEndH = UPPER.h0 + UPPER.rise * UPPER.rows;
  E([upEndD, upEndH], [upEndD, 28.0], C('#4b5059'), [-1, 0]);
  E([upEndD, 28.0], [ROOF.frontD, ROOF.frontH], C('#aeb4bc'), [0, -1]);
  E([ROOF.frontD, ROOF.frontH], [ROOF.frontD, ROOF.frontH + 1.0], C('#e9ecef'), [-1, 0]);
  E([ROOF.frontD, ROOF.frontH + 1.0], [upEndD + 0.6, 29.0], C('#cfd4da'), [0, 1]);
  E([upEndD + 0.6, 29.0], [upEndD + 0.6, -0.1], C('#5d636c'), [1, 0]);

  const soup = new TriSoup();
  const va = new THREE.Vector3(), vb = new THREE.Vector3(), vc = new THREE.Vector3(), vd = new THREE.Vector3(), f = new THREE.Vector3();
  for (let j = 0; j < n; j++) {
    const p = path[j]!, q = path[(j + 1) % n]!;
    const mnx = (p.nx + q.nx) / 2, mnz = (p.nz + q.nz) / 2;
    for (const e of edges) {
      va.set(p.x + p.nx * e.a[0], e.a[1], p.z + p.nz * e.a[0]);
      vb.set(q.x + q.nx * e.a[0], e.a[1], q.z + q.nz * e.a[0]);
      vc.set(q.x + q.nx * e.b[0], e.b[1], q.z + q.nz * e.b[0]);
      vd.set(p.x + p.nx * e.b[0], e.b[1], p.z + p.nz * e.b[0]);
      f.set(mnx * e.facing[0], e.facing[1], mnz * e.facing[0]);
      soup.quad(va, vb, vc, vd, e.color, f);
    }
  }
  // Outer ground so aerial cameras don't see a void.
  const gnd = C('#3b4433');
  const G = 400;
  soup.quad(va.set(-G, -0.15, -G), vb.set(G, -0.15, -G), vc.set(G, -0.15, G), vd.set(-G, -0.15, G), gnd, f.set(0, 1, 0));
  const bowlGeometry = soup.toGeometry();

  // ------------------------------------------------------------------ gantry
  const nearZ = (d: number) => -(BOWL.B + d);
  const gantryD0 = 12.0;
  for (const gx of [0, -36, 36]) {
    const w = gx === 0 ? 24 : 7;
    const zc = nearZ((gantryD0 + FASCIA.d) / 2);
    sb.box(w, 0.3, FASCIA.d - gantryD0, gx, FASCIA.h1 - 0.15, zc, '#2b2f36');
    sb.box(w, 0.9, 0.05, gx, FASCIA.h1 + 0.45, nearZ(gantryD0), '#3b4049');
    sb.box(w, 0.08, 0.08, gx, FASCIA.h1 + 1.0, nearZ(gantryD0), '#c9ccd1');
    // under-bracing
    sb.box(w, 0.5, 0.1, gx, FASCIA.h1 - 0.5, nearZ(gantryD0 + 0.2), '#22262c');
    // camera units
    const cams = gx === 0 ? [-2.0, 0, 2.0] : [0];
    for (const cx of cams) {
      const x = gx + cx, z = nearZ(gantryD0 + 1.0);
      sb.cylinder(0.05, 0.08, 1.1, x, FASCIA.h1 + 0.55, z, '#1a1a1a', 6);
      sb.box(0.35, 0.35, 0.6, x, FASCIA.h1 + 1.3, z + 0.1, '#e6e6e6');
      sb.box(0.22, 0.22, 0.3, x, FASCIA.h1 + 1.3, z + 0.5, '#111111');
    }
  }
  // commentary boxes behind the main gantry (glazed band on the fascia)
  sb.box(30, 1.4, 0.1, 0, FASCIA.h0 + 0.9, nearZ(FASCIA.d - 0.02), '#0f1d2b');

  // ------------------------------------------------------------------ dugouts
  for (const s of [-1, 1]) {
    const team = s < 0 ? config.home : config.away;
    const x = s * DUGOUT.x, z = DUGOUT.z;
    const L = DUGOUT.len, D = DUGOUT.depth;
    sb.box(L + 0.4, 0.15, D, x, 0.075, z, '#2c3036');
    sb.box(L + 0.4, 2.2, 0.12, x, 1.1, z - D / 2, '#2a2f37');
    sb.box(L + 0.6, 0.12, D + 0.4, x, 2.25, z + 0.15, '#cfd6de', 0, -0.12);
    for (const ex of [-1, 1]) sb.box(0.1, 2.1, D, x + ex * (L / 2 + 0.15), 1.05, z, '#5b6b7a');
    sb.box(L + 0.4, 0.12, 0.1, x, 2.15, z + D / 2 + 0.2, team.primaryColor);
    for (let k = 0; k < 7; k++) {
      const sx = x - L / 2 + 0.6 + k * ((L - 1.2) / 6);
      sb.box(0.5, 0.45, 0.45, sx, 0.3, z - 0.55, '#202428');
      sb.box(0.5, 0.6, 0.1, sx, 0.7, z - 0.8, team.primaryColor);
    }
  }

  // ------------------------------------------------------------------ boards (housing) + LED faces
  const led = { pos: [] as number[], uv: [] as number[], nor: [] as number[] };
  const BH = 0.9, tilt = 0.18, U = LED_PANEL_METRES * 4;
  const ledQuad = (ax: number, az: number, bx: number, bz: number, nx: number, nz: number, y0: number, y1: number, u0: number, back: number) => {
    const len = Math.hypot(bx - ax, bz - az);
    const u1 = u0 + len / U;
    const tx = -nx * back, tz = -nz * back;
    const p = [ax, y0, az, bx, y0, bz, bx + tx, y1, bz + tz, ax + tx, y1, az + tz];
    const uv = [u0, 0.03, u1, 0.03, u1, 0.97, u0, 0.97];
    const idx = [0, 1, 2, 0, 2, 3];
    for (const i of idx) {
      led.pos.push(p[i * 3]!, p[i * 3 + 1]!, p[i * 3 + 2]!);
      led.uv.push(uv[i * 2]!, uv[i * 2 + 1]!);
      led.nor.push(nx, 0.2, nz);
    }
    return u1;
  };
  const board = (ax: number, az: number, bx: number, bz: number, nx: number, nz: number, u0: number) => {
    const u = ledQuad(ax, az, bx, bz, nx, nz, 0.05, 0.05 + BH, u0, Math.sin(tilt) * BH);
    // housing behind
    const len = Math.hypot(bx - ax, bz - az);
    const ang = Math.atan2(-(bz - az), bx - ax);
    sb.box(len, BH + 0.1, 0.25, (ax + bx) / 2 - nx * 0.22, (BH + 0.1) / 2, (az + bz) / 2 - nz * 0.22, '#15181c', ang, 0, 0);
    return u;
  };
  // ensure CCW-consistent winding: we only need visible front faces (material is FrontSide via normal flip below).
  const Z = BOARDS.sideZ, X = BOARDS.endX, gap = BOARDS.dugoutGap;
  let u = 0;
  u = board(55, Z, -55, Z, 0, -1, u);                    // far side, faces -z (+x is screen-left from the main cam)
  board(-55, -Z, -gap, -Z, 0, 1, 0.13);                   // near left
  board(gap, -Z, 55, -Z, 0, 1, 0.61);                     // near right
  board(-X, 36, -X, -36, 1, 0, 0.37);                     // left end faces +x
  board(X, -36, X, 36, -1, 0, 0.83);                      // right end faces -x
  // ribbon board along the upper-tier fascia (whole bowl)
  let ru = 0;
  for (let j = 0; j < n; j++) {
    const p = path[j]!, q = path[(j + 1) % n]!;
    const d = FASCIA.d - 0.03;
    ru = ledQuad(p.x + p.nx * d, p.z + p.nz * d, q.x + q.nx * d, q.z + q.nz * d, -(p.nx + q.nx) / 2, -(p.nz + q.nz) / 2, FASCIA.h0 + 0.35, FASCIA.h0 + 1.45, ru, 0);
  }
  const ledGeometry = new THREE.BufferGeometry();
  ledGeometry.setAttribute('position', new THREE.Float32BufferAttribute(led.pos, 3));
  ledGeometry.setAttribute('uv', new THREE.Float32BufferAttribute(led.uv, 2));
  ledGeometry.setAttribute('normal', new THREE.Float32BufferAttribute(led.nor, 3));
  ledGeometry.computeBoundingSphere();

  // ------------------------------------------------------------------ floodlights
  const lamps: number[] = [];
  const cx = BOWL.A - BOWL.R, cz = BOWL.B - BOWL.R;
  for (const sx of [-1, 1]) for (const sz of [-1, 1]) {
    const dx = sx * Math.SQRT1_2, dz = sz * Math.SQRT1_2;
    const off = BOWL.R + 44;
    const px = sx * cx + dx * off, pz = sz * cz + dz * off;
    const H = 58;
    sb.box(1.4, H, 1.4, px, H / 2, pz, '#9aa1a9', Math.atan2(dx, dz));
    // lattice hints
    for (let k = 1; k < 8; k++) sb.box(2.0, 0.25, 2.0, px, (H * k) / 8, pz, '#7d848c', Math.atan2(dx, dz));
    // head: faces the centre, tilted down
    const face = Math.atan2(-dx, -dz);
    sb.box(10, 6, 0.8, px - dx * 0.6, H + 2, pz - dz * 0.6, '#545b63', face, 0.45);
    for (let r = 0; r < 4; r++) for (let c = 0; c < 6; c++) {
      const lx = (c - 2.5) * 1.55, ly = (r - 1.5) * 1.35;
      // local -> world: right vector perpendicular to facing dir
      const rx = Math.cos(face), rz = -Math.sin(face);
      const up = ly * Math.cos(0.45) - 0.5 * Math.sin(0.45);
      const fwd = ly * Math.sin(0.45) + 0.5 * Math.cos(0.45);
      lamps.push(px - dx * 0.6 + rx * lx - dx * fwd, H + 2 + up, pz - dz * 0.6 + rz * lx - dz * fwd, 3.2);
    }
  }
  // roof-edge lights along the two long sides and ends
  for (let j = 0; j < n; j++) {
    const p = path[j]!;
    if (Math.abs(p.nx) > 0.2 && Math.abs(p.nz) > 0.2) continue; // skip corners
    const d = ROOF.frontD + 0.6;
    const x = p.x + p.nx * d, z = p.z + p.nz * d;
    // straights are sampled every ~10m; add 3 lamps per sample spacing
    for (let k = 0; k < 3; k++) {
      const q = path[(j + 1) % n]!;
      if (Math.abs(q.nx - p.nx) > 0.01) break;
      const t = k / 3;
      const lx = x + (q.x - p.x) * t, lz = z + (q.z - p.z) * t;
      sb.box(1.2, 0.5, 0.8, lx, ROOF.frontH - 0.3, lz, '#3d434a', Math.atan2(p.nx, p.nz));
      lamps.push(lx - p.nx * 0.5, ROOF.frontH - 0.45, lz - p.nz * 0.5, 2.0);
    }
  }

  // ------------------------------------------------------------------ seats
  const rnd = prng(config.seed ^ 0x5eed);
  const seatsArr: number[] = [];
  const addRow = (d: number, y: number) => {
    // offset polyline at distance d
    for (let j = 0; j < n; j++) {
      const p = path[j]!, q = path[(j + 1) % n]!;
      const ax = p.x + p.nx * d, az = p.z + p.nz * d, bx = q.x + q.nx * d, bz = q.z + q.nz * d;
      const len = Math.hypot(bx - ax, bz - az);
      const count = Math.floor(len / 0.6);
      const nx = (p.nx + q.nx) / 2, nz = (p.nz + q.nz) / 2;
      for (let k = 0; k < count; k++) {
        if (rnd() < 0.07) continue; // empty seat
        const t = (k + 0.5 + (rnd() - 0.5) * 0.25) / count;
        const x = ax + (bx - ax) * t, z = az + (bz - az) * t;
        // gantry block / tunnel gap
        const angle = Math.atan2(-nx, -nz);
        let code = 2;
        if (x < -PITCH.halfL) code = 0; else if (x > PITCH.halfL) code = 1; else code = z < 0 ? 2 : 3;
        seatsArr.push(x, y, z, angle, code);
      }
    }
  };
  for (let i = 0; i < LOWER.rows; i++) addRow(LOWER.d0 + LOWER.depth * (i + 0.45), LOWER.h0 + LOWER.rise * (i + 1));
  for (let j = 0; j < UPPER.rows; j++) addRow(UPPER.d0 + UPPER.depth * (j + 0.45), UPPER.h0 + UPPER.rise * (j + 1));
  const seats = new Float32Array(seatsArr);

  const anchors: Record<string, Vec3> = {
    gantry_main: { x: 0, y: FASCIA.h1 + 1.5, z: nearZ(gantryD0 - 0.6) },
    gantry_18_left: { x: -36, y: FASCIA.h1 + 1.5, z: nearZ(gantryD0 - 0.6) },
    gantry_18_right: { x: 36, y: FASCIA.h1 + 1.5, z: nearZ(gantryD0 - 0.6) },
    reverse_gantry: { x: 0, y: FASCIA.h1 + 1.5, z: BOWL.B + FASCIA.d - 2.6 },
    behind_goal_left: { x: -(BOWL.A + 1.5), y: 3.2, z: 0 },
    behind_goal_right: { x: BOWL.A + 1.5, y: 3.2, z: 0 },
    pitchside_left: { x: -30, y: 1.6, z: -36.5 },
    pitchside_right: { x: 30, y: 1.6, z: -36.5 },
    spidercam_home: { x: 0, y: 24, z: 0 },
    blimp: { x: 0, y: 140, z: -90 },
    roof_centre_near: { x: 0, y: ROOF.frontH, z: nearZ(ROOF.frontD) },
    dugout_home: { x: -DUGOUT.x, y: 1.0, z: DUGOUT.z },
    dugout_away: { x: DUGOUT.x, y: 1.0, z: DUGOUT.z },
    technical_area_home: { x: -DUGOUT.x, y: 1.0, z: -36.4 },
    technical_area_away: { x: DUGOUT.x, y: 1.0, z: -36.4 },
    tunnel: { x: 0, y: 1.0, z: -(BOWL.B - 0.5) },
    corner_near_left: { x: -PITCH.halfL, y: 0.8, z: -PITCH.halfW },
    corner_near_right: { x: PITCH.halfL, y: 0.8, z: -PITCH.halfW },
    corner_far_left: { x: -PITCH.halfL, y: 0.8, z: PITCH.halfW },
    corner_far_right: { x: PITCH.halfL, y: 0.8, z: PITCH.halfW },
    goal_left: { x: -PITCH.halfL, y: 1.22, z: 0 },
    goal_right: { x: PITCH.halfL, y: 1.22, z: 0 },
    centre_spot: { x: 0, y: 0, z: 0 },
    crowd_home_end: { x: -(BOWL.A + 8), y: 5.5, z: 0 },
    crowd_away_end: { x: BOWL.A + 8, y: 5.5, z: 0 },
    crowd_away_section: { x: BOWL.A + 8, y: 5.5, z: -16 },
    crowd_main_stand: { x: 0, y: 5.5, z: -(BOWL.B + 8) },
    crowd_far_stand: { x: 0, y: 5.5, z: BOWL.B + 8 },
    crowd_far_upper: { x: 0, y: 19, z: BOWL.B + 25 },
    crowd_corner_near_left: { x: -(BOWL.A - BOWL.R + Math.SQRT1_2 * (BOWL.R + 8)), y: 6, z: -(BOWL.B - BOWL.R + Math.SQRT1_2 * (BOWL.R + 8)) },
    crowd_corner_far_right: { x: BOWL.A - BOWL.R + Math.SQRT1_2 * (BOWL.R + 8), y: 6, z: BOWL.B - BOWL.R + Math.SQRT1_2 * (BOWL.R + 8) },
  };

  return { seats, seatCount: seats.length / 5, lamps, ledGeometry, bowlGeometry, anchors };
}
